import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Ruler, TreePine, Users, Clock, Sparkles, Shield } from 'lucide-react';

const benefits = [
  {
    icon: Ruler,
    title: 'Design personalizat',
    desc: 'Fiecare piesă de mobilier este proiectată special pentru spațiul și nevoile tale.',
  },
  {
    icon: TreePine,
    title: 'Materiale premium',
    desc: 'Folosim doar lemn și materiale de cea mai înaltă calitate, certificate și durabile.',
  },
  {
    icon: Users,
    title: 'Echipă profesionistă',
    desc: 'Meșteri cu experiență, designeri creativi și manageri de proiect dedicați.',
  },
  {
    icon: Clock,
    title: 'Livrare la timp',
    desc: 'Respectăm termenele stabilite. Planificăm riguros fiecare etapă a proiectului.',
  },
  {
    icon: Sparkles,
    title: 'Atenție la detalii',
    desc: 'De la cantine și balamale până la finisajul final — fiecare detaliu contează.',
  },
  {
    icon: Shield,
    title: 'Garanție extinsă',
    desc: 'Oferim garanție pentru toate produsele noastre, pentru liniștea ta.',
  },
];

export default function WhyChooseUsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="avantaje" data-testid="why-choose-us-section" className="py-20 lg:py-32 bg-[#EAE4DA] dark:bg-[#2E2C2A]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#A67B5B] mb-4">
            Avantajele noastre
          </p>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl tracking-tight font-medium text-[#2C2A29] dark:text-[#F0EDE8]">
            De ce să ne alegi pe noi
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {benefits.map((b, i) => (
            <BenefitCard key={b.title} benefit={b} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function BenefitCard({ benefit, index }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const Icon = benefit.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      data-testid={`benefit-card-${index}`}
      className="bg-white dark:bg-[#242322] rounded-lg p-8 lg:p-10 hover:shadow-lg transition-shadow duration-500"
    >
      <div className="w-12 h-12 rounded-full bg-[#A67B5B]/10 dark:bg-[#A67B5B]/20 flex items-center justify-center mb-5">
        <Icon size={22} className="text-[#A67B5B]" />
      </div>
      <h3 className="font-['Outfit'] text-xl font-medium text-[#2C2A29] dark:text-[#F0EDE8] mb-3">
        {benefit.title}
      </h3>
      <p className="text-sm leading-relaxed text-[#6B6561] dark:text-[#9B9590]">
        {benefit.desc}
      </p>
    </motion.div>
  );
}
